import { useEffect, useState } from "react";
import { useGetPlaybooksSettings } from "../api/useGetPlaybooksSettings";
import { useUpdatePlaybooksSettings } from "../api/useUpdatePlaybooksSettings";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  sendEvent,
  EventName,
  EventParameter,
} from "@/shared/utils/UsageAnalytics";

export default function PlaybooksSettings() {
  const {
    playbooksSettings,
    getPlaybooksSettings,
    setPlaybooksSettings,
    isLoading,
  } = useGetPlaybooksSettings();

  const {
    updatePlaybooksSettings,
    updatedPlaybookSettings,
    isLoading: isUpdating,
    isFailed: isUpdateFailed,
  } = useUpdatePlaybooksSettings();

  const [notifyOnCompletion, setNotifyOnCompletion] = useState(false);

  useEffect(() => {
    getPlaybooksSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (playbooksSettings) {
      setNotifyOnCompletion(playbooksSettings.notifyOnCompletion);
    }
  }, [playbooksSettings, setNotifyOnCompletion]);

  useEffect(() => {
    if (updatedPlaybookSettings && !isUpdateFailed) {
      setPlaybooksSettings(updatedPlaybookSettings);
    }
    if (isUpdateFailed) {
      toast.error("Failed to update playbooks settings");
    }
  }, [updatedPlaybookSettings, isUpdateFailed, setPlaybooksSettings]);

  const handleNotifyOnCompletionChange = async (checked: boolean) => {
    setNotifyOnCompletion(checked);

    // Send analytics event
    sendEvent(EventName.SettingsChanged, {
      [EventParameter.SettingName]: "playbook_notifications",
      [EventParameter.SettingValue]: checked.toString(),
    });

    await updatePlaybooksSettings({
      notifyOnCompletion: checked,
    });
  };

  if (isLoading) {
    return (
      <div className="flex-1 px-6 py-3">
        <h2 className="text-lg font-semibold mb-4">Playbooks Settings</h2>
        <div className="text-sm text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 px-6 py-3">
      <h2 className="text-lg font-semibold mb-4">Playbooks Settings</h2>

      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="notify-completion"
            checked={notifyOnCompletion}
            onCheckedChange={handleNotifyOnCompletionChange}
            disabled={isUpdating}
          />
          <Label
            htmlFor="notify-completion"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Notify on completion
          </Label>
        </div>
        <p className="text-sm text-gray-600">
          Receive notifications when playbook execution is completed.
        </p>
      </div>
    </div>
  );
}
